import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../common';
import { getGraphQLClient, GENERATE_MCP_TOKEN } from '../../lib/graphql';

type GenerateResult = {
  generateMcpToken: {
    token: string | null;
    expiresAt: string | null;
    errors: string[];
  };
};

const MCP_ENDPOINT = `${window.location.origin}/mcp`;

const claudeCodeCommand = (token: string) =>
  `claude mcp add book-typist ${MCP_ENDPOINT} --transport http --header "Authorization: Bearer ${token}"`;

const copilotConfig = (token: string) =>
  JSON.stringify(
    {
      servers: {
        'book-typist': {
          type: 'http',
          url: MCP_ENDPOINT,
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );

export default function McpIntegrationPanel() {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const generate = async () => {
    setIsGenerating(true);
    setErrors([]);
    try {
      const data = await getGraphQLClient().request<GenerateResult>(GENERATE_MCP_TOKEN, {
        expiresInDays: 365,
      });
      const result = data.generateMcpToken;
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setToken(result.token);
        setExpiresAt(result.expiresAt);
        setRevealed(true);
      }
    } catch (e) {
      console.error('MCP token generation failed', e);
      setErrors(['Failed to generate token']);
    } finally {
      setIsGenerating(false);
    }
  };

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((current) => (current === key ? null : current)), 2000);
    } catch (e) {
      console.error('Clipboard write failed', e);
    }
  };

  const masked = token ? `${token.slice(0, 16)}…${token.slice(-8)}` : '';
  const displayToken = revealed && token ? token : masked;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="card p-6 mt-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        MCP integration
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Connect Claude Code, GitHub Copilot, or any MCP-compatible client to read and edit your
        books directly from the editor. Generates a long-lived API token (365 days) — treat it
        like a password.
      </p>

      {!token && (
        <div className="flex items-center gap-3">
          <Button onClick={generate} isLoading={isGenerating}>
            Generate MCP token
          </Button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Existing tokens stay valid; this issues an additional one.
          </span>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          {errors.map((e, i) => (
            <p key={i} className="text-sm text-red-600 dark:text-red-400">
              {e}
            </p>
          ))}
        </div>
      )}

      {token && (
        <div className="space-y-6">
          <CodeBlock
            label="Token"
            value={displayToken}
            copyValue={token}
            copied={copied === 'token'}
            onCopy={() => copy('token', token)}
            extra={
              <>
                <button
                  type="button"
                  onClick={() => setRevealed((v) => !v)}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {revealed ? 'Hide' : 'Reveal'}
                </button>
                {expiresAt && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Expires {new Date(expiresAt).toLocaleDateString()}
                  </span>
                )}
              </>
            }
          />

          <CodeBlock
            label="Claude Code"
            description="Run this once in your terminal."
            value={claudeCodeCommand(token)}
            copied={copied === 'claude'}
            onCopy={() => copy('claude', claudeCodeCommand(token))}
          />

          <CodeBlock
            label="GitHub Copilot (VS Code)"
            description="Save as .vscode/mcp.json in any workspace."
            value={copilotConfig(token)}
            copied={copied === 'copilot'}
            onCopy={() => copy('copilot', copilotConfig(token))}
            multiline
          />

          <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button variant="ghost" onClick={generate} isLoading={isGenerating}>
              Generate another
            </Button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Endpoint: <code className="font-mono">{MCP_ENDPOINT}</code>
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}

type CodeBlockProps = {
  label: string;
  description?: string;
  value: string;
  copyValue?: string;
  copied: boolean;
  onCopy: () => void;
  multiline?: boolean;
  extra?: React.ReactNode;
};

function CodeBlock({
  label,
  description,
  value,
  copied,
  onCopy,
  multiline = false,
  extra,
}: CodeBlockProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1 gap-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
          {extra}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{description}</p>
      )}
      <pre
        className={`p-3 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-800 dark:text-gray-200 ${
          multiline ? 'whitespace-pre overflow-x-auto' : 'whitespace-pre-wrap break-all'
        }`}
      >
        {value}
      </pre>
    </div>
  );
}
